import clsx from 'clsx'
import { useCallback } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { AccountAddress } from '../AccountAddressBook/AccountAddressBook'
import LoadingButton from '../Button/LoadingButton'
import { AddressInput } from '../Form'
import Modal from '../Modal/Modal'
import Notification from '../Notification/Notification'

export interface AccountAddressBookAddModalProps {
  /**
   * Error from the save attempt, shown above the form
   */
  error?: Error | null
  isCreatingAddress?: boolean
  /**
   * Callback on submission
   */
  onSubmit: (address: Omit<AccountAddress, 'id'>) => void
}

export default function AccountAddressBookAddModal({
  error = null,
  isCreatingAddress = false,
  onSubmit,
}: AccountAddressBookAddModalProps) {
  const methods = useForm()
  const { handleSubmit, control } = methods

  const submitForm = useCallback(
    (data: FieldValues) =>
      onSubmit({
        address1: data.address.address_1 || null,
        address2: data.address.address_2 || null,
        attentionName: data.address.attention_name || null,
        city: data.address.city || null,
        companyName: data.address.company_name || null,
        countryCode: data.address.country_code || null,
        facility: null,
        firstName: null,
        lastName: null,
        phoneNumber: data.address.phone_number || null,
        state: data.address.state || null,
        zip: data.address.zip || null,
      }),
    [onSubmit],
  )

  return (
    <Modal
      id="account-address-book-add-modal"
      title="Add address"
      className="[&]:p-12"
      dialogPanelClass="[&]:max-w-[21.44rem] [&]:md:max-w-[35.31rem] w-full [&]:overflow-visible"
      content={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            {error?.message && (
              <div className="mb-4">
                <Notification title={error.message} type={'urgent'} />
              </div>
            )}
            <AddressInput
              className={clsx(
                'w-full [&_.comboboxoptions]:sm-max:max-h-[200px] mb-4',
              )}
              control={control}
              name="address"
              showContact={false}
              type="destination"
            />
            <LoadingButton
              className="w-full"
              loading={isCreatingAddress}
              type="submit"
            >
              Save
            </LoadingButton>
          </form>
        </FormProvider>
      }
    />
  )
}
